import React, { useState, useEffect, useMemo, useCallback, lazy, Suspense } from 'react';
import { Search, RefreshCw, ChevronLeft, ChevronRight, Pencil, Check, X, Wand2, Plus } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Badge, Spinner } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { supabase } from '@/lib/supabase/client';
import { parseTurnCode, getHexColor, STANDARD_COLORS, type TurnColorMapping } from '@/lib/turn-color';
import { suggestTurnCode, suggestSkillId, type SkillOption } from '@/services/scan/turn-code-review';
import { toast } from 'sonner';
import { FEATURES } from '@/lib/feature-flags';
import type { TurnColorWord, GoogleSheetsConfig } from '@/types/turnColor';

const ScanningPipeline = lazy(() => import('./ScanningPipeline').then(m => ({ default: m.ScanningPipeline })));

const ITEMS_PER_PAGE = 50;

interface LexiconPageProps {
  config: GoogleSheetsConfig;
  onBack: () => void;
  onRefresh?: () => void;
  refreshTrigger?: number;
}

type StatusFilter = 'all' | 'valid' | 'invalid';
type LexiconTab = 'lexicon' | 'pipeline';

function getGPValues(turnCode: string | null | undefined): string {
  if (!turnCode) return '—';
  const matches = [...turnCode.matchAll(/\[([^\]]+)\]/g)].map(m => m[1]);
  return matches.length > 0 ? matches.join(', ') : '—';
}

function isValidTurnCode(turnCode: string | null | undefined): boolean {
  if (!turnCode?.trim()) return false;
  try {
    const segments = parseTurnCode(turnCode);
    return segments.some(s => s.type === 'color' && s.color &&
      STANDARD_COLORS.some(c => c.color_name.toLowerCase() === s.color!.toLowerCase()));
  } catch {
    return false;
  }
}

export const LexiconPage: React.FC<LexiconPageProps> = ({ onBack, onRefresh, refreshTrigger }) => {
  const [activeTab, setActiveTab] = useState<LexiconTab>('lexicon');
  const [words, setWords] = useState<TurnColorWord[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<StatusFilter>('all');
  const [filterSkillId, setFilterSkillId] = useState('all');
  const [filterTeachable, setFilterTeachable] = useState(false);
  const [filterLength, setFilterLength] = useState<'all' | number>('all');
  const [currentPage, setCurrentPage] = useState(0);

  const [skills, setSkills] = useState<SkillOption[]>([]);
  const colorMappings = STANDARD_COLORS;

  const [editingWordId, setEditingWordId] = useState<string | null>(null);
  const [editTurnCode, setEditTurnCode] = useState('');
  const [editSkillId, setEditSkillId] = useState('');
  const [saving, setSaving] = useState(false);

  const [showAddWord, setShowAddWord] = useState(false);
  const [newWord, setNewWord] = useState('');
  const [addingSaving, setAddingSaving] = useState(false);

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  const loadWords = useCallback(async (page: number, search?: string) => {
    try {
      setLoading(true);
      setError(null);

      const from = page * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;

      let query = supabase
        .from('turn_color_words')
        .select('id, word, turn_code, skill_id, phase, length, syllables, definition, notes, difficulty, category, teachable, verified, skill_verified', { count: 'exact' })
        .order('word')
        .range(from, to);

      if (filterLength !== 'all') {
        query = query.eq('length', filterLength);
      }

      // Server-side skill filter (must be server-side since data is paginated)
      if (filterSkillId !== 'all') {
        query = query.eq('skill_id', filterSkillId);
      }

      // Server-side search filter
      if (search) {
        query = query.ilike('word', `%${search}%`);
      }

      const { data, error: fetchErr, count } = await query;

      if (fetchErr) throw new Error(fetchErr.message);
      setWords(data ?? []);
      setTotalCount(count ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load words');
    } finally {
      setLoading(false);
    }
  }, [filterLength, filterSkillId]);

  const loadSkills = useCallback(async () => {
    const { data } = await supabase
      .from('skill_sequence')
      .select('skill_id, color, phase, display_name, sequence_order')
      .order('sequence_order');
    if (data) setSkills(data as SkillOption[]);
  }, []);

  useEffect(() => {
    loadWords(0, searchTerm || undefined);
    loadSkills();
  }, [loadWords, loadSkills]);

  // Reload from DB when search term changes (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(0);
      loadWords(0, searchTerm || undefined);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, loadWords]);

  // Reset pagination when skill filter changes
  useEffect(() => {
    setCurrentPage(0);
    loadWords(0, searchTerm || undefined);
  }, [filterSkillId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      loadWords(currentPage, searchTerm || undefined);
    }
  }, [refreshTrigger, loadWords, currentPage, searchTerm]);

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    loadWords(currentPage, searchTerm || undefined).finally(() => setRefreshing(false));
    if (onRefresh) onRefresh();
  }, [loadWords, currentPage, searchTerm, onRefresh]);

  // ---------------------------------------------------------------------------
  // Filtering (client-side for status/skill/teachable; search is server-side)
  // ---------------------------------------------------------------------------

  const filteredWords = useMemo(() => {
    let list = words;

    if (filterStatus === 'valid') {
      list = list.filter(w => isValidTurnCode(w.turn_code));
    } else if (filterStatus === 'invalid') {
      list = list.filter(w => !isValidTurnCode(w.turn_code));
    }

    if (filterSkillId !== 'all') {
      list = list.filter(w => w.skill_id === filterSkillId);
    }

    if (filterTeachable) {
      list = list.filter(w => w.teachable === true);
    }

    return list;
  }, [words, filterStatus, filterSkillId, filterTeachable]);

  // ---------------------------------------------------------------------------
  // Stats (computed from full page of data — approximate for display)
  // ---------------------------------------------------------------------------

  const stats = useMemo(() => {
    const valid = words.filter(w => isValidTurnCode(w.turn_code)).length;
    const uniqueSkills = new Set(words.map(w => w.skill_id).filter(Boolean));
    const teachableCount = words.filter(w => w.teachable).length;
    return {
      totalWords: totalCount,
      validTurnCodes: valid,
      pageValid: valid,
      pageTotal: words.length,
      skillsUsed: uniqueSkills.size,
      teachableCount,
    };
  }, [words, totalCount]);

  // ---------------------------------------------------------------------------
  // Pagination
  // ---------------------------------------------------------------------------

  const handleLengthChange = useCallback((value: 'all' | number) => {
    setCurrentPage(0);
    setFilterLength(value);
  }, []);

  const totalPages = Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE));

  const goToPage = useCallback((page: number) => {
    if (page >= 0 && page < totalPages) {
      setCurrentPage(page);
      loadWords(page, searchTerm || undefined);
    }
  }, [totalPages, loadWords, searchTerm]);

  // ---------------------------------------------------------------------------
  // Inline editing
  // ---------------------------------------------------------------------------

  const handleStartEdit = (word: TurnColorWord) => {
    setEditingWordId(word.id);
    setEditTurnCode(word.turn_code || suggestTurnCode(word.word));
    const autoSkill = suggestSkillId(word.turn_code || suggestTurnCode(word.word), skills);
    setEditSkillId(word.skill_id || autoSkill || '');
  };

  const handleCancelEdit = () => {
    setEditingWordId(null);
    setEditTurnCode('');
    setEditSkillId('');
  };

  const handleToggleVerified = async (word: TurnColorWord) => {
    const next = !word.verified;
    setWords(prev => prev.map(w => w.id === word.id ? { ...w, verified: next } : w));
    const { error } = await supabase
      .from('turn_color_words')
      .update({ verified: next })
      .eq('id', word.id);
    if (error) {
      setWords(prev => prev.map(w => w.id === word.id ? { ...w, verified: word.verified } : w));
      toast.error('Failed to update verified status');
    }
  };

  const handleToggleSkillVerified = async (word: TurnColorWord) => {
    const next = !word.skill_verified;
    setWords(prev => prev.map(w => w.id === word.id ? { ...w, skill_verified: next } : w));
    const { error } = await supabase
      .from('turn_color_words')
      .update({ skill_verified: next })
      .eq('id', word.id);
    if (error) {
      setWords(prev => prev.map(w => w.id === word.id ? { ...w, skill_verified: word.skill_verified } : w));
      toast.error('Failed to update skill verified status');
    }
  };

  const handleToggleTeachable = async (word: TurnColorWord) => {
    const next = !word.teachable;
    // Optimistic update
    setWords(prev => prev.map(w => w.id === word.id ? { ...w, teachable: next } : w));
    const { error } = await supabase
      .from('turn_color_words')
      .update({ teachable: next })
      .eq('id', word.id);
    if (error) {
      // Revert on failure
      setWords(prev => prev.map(w => w.id === word.id ? { ...w, teachable: word.teachable } : w));
      toast.error('Failed to update teachable status');
    }
  };

  const handleColorChipClick = (colorName: string) => {
    setEditTurnCode(prev => prev.replace('___', colorName));
  };

  const remainingPlaceholders = (editTurnCode.match(/___/g) || []).length;

  const handleSave = async () => {
    if (!editingWordId || !editTurnCode.trim()) return;
    setSaving(true);

    const wordObj = words.find(w => w.id === editingWordId);
    if (!wordObj) { setSaving(false); return; }

    try {
      const updateFields: Record<string, unknown> = {
        turn_code: editTurnCode.trim(),
        updated_at: new Date().toISOString(),
      };
      updateFields.skill_id = FEATURES.SKILL_PROGRESSION ? (editSkillId || null) : null;

      const { data, error: updateErr } = await supabase
        .from('turn_color_words')
        .update(updateFields)
        .eq('id', editingWordId)
        .select('id');

      if (updateErr) {
        toast.error(updateErr.message || 'Failed to save');
        return;
      }

      if (!data || data.length === 0) {
        toast.error('Update was not applied — you may not have write access');
        return;
      }

      toast.success(`Saved turn code for "${wordObj.word}"`);
      setWords(prev => prev.map(w =>
        w.id === editingWordId
          ? { ...w, turn_code: editTurnCode.trim(), skill_id: editSkillId || w.skill_id }
          : w
      ));
      handleCancelEdit();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Add new word
  // ---------------------------------------------------------------------------

  const handleAddWord = async () => {
    const trimmed = newWord.trim().toLowerCase();
    if (!trimmed) return;

    setAddingSaving(true);
    try {
      const suggested = suggestTurnCode(trimmed);

      // Check if word already exists
      const { data: existing } = await supabase
        .from('turn_color_words')
        .select('id')
        .eq('word', trimmed)
        .maybeSingle();

      if (existing) {
        toast.info(`"${trimmed}" already exists — showing it below`);
        setNewWord('');
        setShowAddWord(false);
        setSearchTerm(trimmed);
        setCurrentPage(0);
        loadWords(0, trimmed);
        return;
      }

      // Insert new word
      const { data, error: insertErr } = await supabase
        .from('turn_color_words')
        .insert({ word: trimmed, turn_code: suggested, length: trimmed.length, notes: 'Added via lexicon' })
        .select('id, word');

      if (insertErr) {
        toast.error(insertErr.message || 'Failed to add word');
        return;
      }

      if (!data || data.length === 0) {
        toast.error('Word was not saved — check database permissions');
        return;
      }

      toast.success(`Added "${trimmed}" — edit its turn code below`);
      setNewWord('');
      setShowAddWord(false);
      // Reload with server-side filter so the new word is visible
      setFilterStatus('all');
      setFilterSkillId('all');
      setFilterTeachable(false);
      setFilterLength('all');
      setSearchTerm(trimmed);
      setCurrentPage(0);
      loadWords(0, trimmed);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add word');
    } finally {
      setAddingSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Turn code preview renderer
  // ---------------------------------------------------------------------------

  const renderTurnCodePreview = (turnCode: string) => {
    try {
      const segments = parseTurnCode(turnCode);
      return (
        <span className="inline-flex gap-px">
          {segments.map((seg, i) => {
            if (seg.type === 'color' && seg.color) {
              const hex = getHexColor(seg.color, colorMappings);
              return (
                <span key={i} style={{ color: hex || '#fff' }} className="font-mono font-bold">
                  {seg.content}
                </span>
              );
            }
            return (
              <span key={i} className="font-mono text-[#1A1A1A]">{seg.content}</span>
            );
          })}
        </span>
      );
    } catch {
      return <span className="text-[#FF453A] font-mono text-xs">Invalid</span>;
    }
  };

  // ---------------------------------------------------------------------------
  // Skill dropdown grouped by phase
  // ---------------------------------------------------------------------------

  const skillsByPhase = useMemo(() => {
    const phases = new Map<string, SkillOption[]>();
    for (const s of skills) {
      const p = s.phase || 'Other';
      if (!phases.has(p)) phases.set(p, []);
      phases.get(p)!.push(s);
    }
    return phases;
  }, [skills]);

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const renderSkillSelect = () => (
    <select
      value={editSkillId}
      onChange={e => setEditSkillId(e.target.value)}
      className="px-2 py-1.5 bg-black/[0.03] border border-[#D4D4D4] rounded text-[#1A1A1A] text-xs focus:outline-none focus:border-[#0A84FF] [&>option]:bg-white [&>optgroup]:bg-white"
    >
      <option value="">No skill</option>
      {Array.from(skillsByPhase.entries()).map(([phase, items]) => (
        <optgroup key={phase} label={`Phase: ${phase}`}>
          {items.map(s => (
            <option key={s.skill_id} value={s.skill_id}>
              {s.skill_id}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );

  const renderEditPanel = () => (
    <div className="space-y-2">
      <input
        type="text"
        value={editTurnCode}
        onChange={e => setEditTurnCode(e.target.value)}
        placeholder="e.g. h, [e-green], l, p"
        className="w-full px-3 py-2 bg-black/[0.03] border border-[#0A84FF] rounded text-[#1A1A1A] text-sm font-mono focus:outline-none"
        autoFocus
        onKeyDown={e => {
          if (e.key === 'Enter') handleSave();
          if (e.key === 'Escape') handleCancelEdit();
        }}
      />
      {remainingPlaceholders > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {colorMappings.map(cm => (
            <button
              key={cm.color_name}
              onClick={() => handleColorChipClick(cm.color_name)}
              className="min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0 px-2 py-1.5 md:px-1.5 md:py-0.5 rounded text-xs md:text-[10px] font-bold bg-black/[0.03] hover:bg-black/[0.06] active:bg-black/[0.08] transition-colors leading-tight flex items-center justify-center"
              title={cm.color_name}
              style={{ color: cm.hex_code }}
            >
              {cm.color_name.slice(0, 2).toUpperCase()}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <span className="text-xs text-[#888888]">Skill:</span>
        {renderSkillSelect()}
      </div>
      {editTurnCode.trim() && (
        <div className="flex items-center gap-2 text-xs text-[#888888]">
          <span>Preview:</span> {renderTurnCodePreview(editTurnCode)}
        </div>
      )}
      <div className="flex items-center gap-2">
        <button
          onClick={handleSave}
          disabled={saving || !editTurnCode.trim()}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#30D158]/20 text-[#30D158] text-sm font-medium transition-colors disabled:opacity-30 min-h-[44px]"
        >
          {saving ? <Spinner size="sm" /> : <Check className="w-4 h-4" />}
          Save
        </button>
        <button
          onClick={handleCancelEdit}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-black/[0.03] text-[#3D3D3D] text-sm font-medium transition-colors min-h-[44px]"
        >
          <X className="w-4 h-4" />
          Cancel
        </button>
      </div>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Loading / error states
  // ---------------------------------------------------------------------------

  if (loading && words.length === 0) {
    return (
      <div className="flex justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <div className="text-center py-8">
          <p className="text-[#FF453A] text-lg font-semibold mb-2">Error Loading Data</p>
          <p className="text-[#888888] text-sm mb-4">{error}</p>
          <Button variant="secondary" size="sm" onClick={onBack}>Go Back</Button>
        </div>
      </Card>
    );
  }

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 rounded-lg hover:bg-black/[0.05] text-[#888888] hover:text-white transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-lg font-semibold text-[#1A1A1A]">Turn Color Lexicon</h2>
            <p className="text-sm text-[#888888] mt-0.5">
              {totalCount.toLocaleString()} words in the lexicon
            </p>
          </div>
        </div>
        {activeTab === 'lexicon' && (
          <div className="flex items-center gap-2 self-start md:self-auto">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowAddWord(v => !v)}
              className="min-h-[44px] md:min-h-0"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Word
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
            className="min-h-[44px] md:min-h-0"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-[#D4D4D4] pb-px">
        {([['lexicon', 'Lexicon'], ['pipeline', 'Scanning Pipeline']] as const).map(([tab, label]) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab
                ? 'border-[#0A84FF] text-white'
                : 'border-transparent text-[#888888] hover:text-[#1A1A1A]'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Pipeline tab */}
      {activeTab === 'pipeline' && (
        <Suspense fallback={<div className="flex justify-center py-20"><Spinner size="lg" /></div>}>
          <ScanningPipeline />
        </Suspense>
      )}

      {/* Lexicon tab content */}
      {activeTab === 'lexicon' && <>

      {/* Add word panel */}
      {showAddWord && (
        <Card className="mb-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label className="block text-xs text-[#888888] mb-1">New Word</label>
              <input
                type="text"
                value={newWord}
                onChange={e => setNewWord(e.target.value)}
                placeholder="Enter a word..."
                className="w-full px-3 py-2.5 md:py-2 bg-black/[0.03] border border-[#D4D4D4] rounded-lg text-[#1A1A1A] text-sm font-mono placeholder-[#D4D4D4] focus:outline-none focus:border-[#0A84FF]"
                autoFocus
                onKeyDown={e => {
                  if (e.key === 'Enter') handleAddWord();
                  if (e.key === 'Escape') { setShowAddWord(false); setNewWord(''); }
                }}
              />
            </div>
            {newWord.trim() && (
              <div className="flex-1">
                <p className="text-xs text-[#888888] mb-1">Suggested Turn Code</p>
                <p className="font-mono text-sm text-[#3D3D3D]">{suggestTurnCode(newWord.trim().toLowerCase())}</p>
              </div>
            )}
            <div className="flex items-center gap-2">
              <button
                onClick={handleAddWord}
                disabled={addingSaving || !newWord.trim()}
                className="flex items-center gap-1.5 px-4 py-2.5 md:py-2 rounded-lg bg-[#0A84FF] text-[#1A1A1A] text-sm font-medium transition-colors disabled:opacity-30 min-h-[44px] md:min-h-0"
              >
                {addingSaving ? <Spinner size="sm" /> : <Plus className="w-4 h-4" />}
                Add
              </button>
              <button
                onClick={() => { setShowAddWord(false); setNewWord(''); }}
                className="flex items-center gap-1.5 px-3 py-2.5 md:py-2 rounded-lg bg-black/[0.03] text-[#3D3D3D] text-sm font-medium transition-colors min-h-[44px] md:min-h-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          {newWord.trim() && (
            <p className="text-xs text-[#888888] mt-2">
              The word will be added with a suggested turn code. You can edit the turn code after adding.
            </p>
          )}
        </Card>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <Card padding="sm">
          <p className="text-2xl font-bold text-[#1A1A1A]">{stats.totalWords.toLocaleString()}</p>
          <p className="text-xs text-[#888888]">Total Words</p>
        </Card>
        <Card padding="sm">
          <p className="text-2xl font-bold text-[#30D158]">{stats.pageValid}</p>
          <p className="text-xs text-[#888888]">Valid Turn Codes (this page)</p>
        </Card>
        <Card padding="sm">
          <p className="text-2xl font-bold text-[#0A84FF]">{stats.skillsUsed}</p>
          <p className="text-xs text-[#888888]">Skills Used (this page)</p>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:ring-1 hover:ring-[#FF9F0A]/40 transition-all" onClick={() => setFilterTeachable(f => !f)}>
          <p className="text-2xl font-bold text-[#FF9F0A]">{stats.teachableCount}</p>
          <p className="text-xs text-[#888888]">Teachable (this page){filterTeachable ? ' — filtered' : ''}</p>
        </Card>
      </div>

      {/* Filter bar */}
      <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center mb-4">
        <div className="relative flex-1 md:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888888]" />
          <input
            type="text"
            placeholder="Search words, turn codes, skills..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 md:py-2 bg-black/[0.03] border border-[#D4D4D4] rounded-lg text-[#1A1A1A] text-sm placeholder-[#D4D4D4] focus:outline-none focus:border-[#0A84FF]"
          />
        </div>

        <select
          value={filterLength}
          onChange={e => handleLengthChange(e.target.value === 'all' ? 'all' : Number(e.target.value))}
          className="px-3 py-2 bg-black/[0.03] border border-[#D4D4D4] rounded-lg text-[#1A1A1A] text-sm focus:outline-none focus:border-[#0A84FF] [&>option]:bg-white"
        >
          <option value="all">All Lengths</option>
          {Array.from({ length: 14 }, (_, i) => i + 2).map(n => (
            <option key={n} value={n}>{n} letters</option>
          ))}
        </select>

        {FEATURES.SKILL_PROGRESSION && (
          <select
            value={filterSkillId}
            onChange={e => setFilterSkillId(e.target.value)}
            className="px-3 py-2 bg-black/[0.03] border border-[#D4D4D4] rounded-lg text-[#1A1A1A] text-sm focus:outline-none focus:border-[#0A84FF] [&>option]:bg-white [&>optgroup]:bg-white"
          >
            <option value="all">All Skills</option>
            {Array.from(skillsByPhase.entries()).map(([phase, items]) => (
              <optgroup key={phase} label={`Phase: ${phase}`}>
                {items.map(s => (
                  <option key={s.skill_id} value={s.skill_id}>
                    {s.skill_id}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        )}

        <div className="flex gap-1">
          {(['all', 'valid', 'invalid'] as const).map(mode => (
            <button
              key={mode}
              onClick={() => setFilterStatus(mode)}
              className={`px-3 py-2 md:py-1.5 rounded-lg text-xs font-medium transition-colors min-h-[44px] md:min-h-0 ${
                filterStatus === mode
                  ? 'bg-[#0A84FF] text-white'
                  : 'bg-black/[0.03] text-[#3D3D3D] hover:bg-black/[0.05]'
              }`}
            >
              {mode === 'all' ? 'All' : mode === 'valid' ? 'Valid' : 'Invalid'}
            </button>
          ))}
        </div>

        <button
          onClick={() => setFilterTeachable(f => !f)}
          className={`px-3 py-2 md:py-1.5 rounded-lg text-xs font-medium transition-colors min-h-[44px] md:min-h-0 ${
            filterTeachable
              ? 'bg-[#FF9F0A] text-black'
              : 'bg-black/[0.03] text-[#3D3D3D] hover:bg-black/[0.05]'
          }`}
        >
          Teachable only
        </button>
      </div>

      {/* Word list — empty state */}
      {filteredWords.length === 0 && (
        <Card>
          <p className="text-center text-[#888888] text-sm py-4">
            No words match your current filters.
          </p>
        </Card>
      )}

      {/* Mobile: card layout */}
      {filteredWords.length > 0 && (
        <div className="md:hidden space-y-2 mb-6">
          {filteredWords.map(w => {
            const isEditing = editingWordId === w.id;
            const valid = isValidTurnCode(w.turn_code);

            return (
              <Card key={w.id} padding="sm">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-base font-semibold text-[#1A1A1A]">{w.word}</span>
                    {valid ? (
                      <Badge variant="success">Valid</Badge>
                    ) : (
                      <Badge variant="error">Invalid</Badge>
                    )}
                    {w.skill_id && (
                      <span className="flex items-center gap-1.5 text-xs text-[#888888]">
                        <button
                          onClick={() => handleToggleSkillVerified(w)}
                          title={w.skill_verified ? 'Skill ID verified — click to unverify' : 'Click to mark skill ID as verified'}
                          className="w-3 h-3 rounded-full transition-all shrink-0"
                          style={{ backgroundColor: w.skill_verified ? '#30D158' : 'rgba(255,255,255,0.15)' }}
                        />
                        Skill: {w.skill_id}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => handleToggleTeachable(w)}
                      className={`px-2.5 py-1.5 rounded-full text-xs font-semibold transition-colors min-h-[44px] flex items-center ${
                        w.teachable === true
                          ? 'bg-[#FF9F0A]/20 text-[#FF9F0A] hover:bg-[#FF9F0A]/30'
                          : w.teachable === false
                          ? 'bg-[#111] text-white hover:bg-[#333]'
                          : 'bg-black/[0.04] text-[#AEAEB2] hover:bg-black/[0.08]'
                      }`}
                      title={w.teachable ? 'Mark as not teachable' : 'Mark as teachable'}
                    >
                      {w.teachable === true ? 'Teachable' : w.teachable === false ? 'No' : '—'}
                    </button>
                    {!isEditing && (
                      <button
                        onClick={() => handleStartEdit(w)}
                        className="p-2.5 rounded-lg hover:bg-black/[0.05] text-[#888888] hover:text-white transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {isEditing ? (
                  renderEditPanel()
                ) : (
                  <div className="space-y-1">
                    {w.turn_code ? (
                      <>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleToggleVerified(w)}
                            title={w.verified ? 'Verified — click to unverify' : 'Click to mark turn code as verified'}
                            className="w-3 h-3 rounded-full transition-all shrink-0"
                            style={{ backgroundColor: w.verified ? '#30D158' : 'rgba(255,255,255,0.15)' }}
                          />
                          <p className="font-mono text-xs text-[#3D3D3D] break-all">{w.turn_code}</p>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-[#888888]">
                          <span>Preview:</span> {renderTurnCodePreview(w.turn_code)}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-[#888888]">
                          <span>G-P Values:</span>
                          <span className="font-mono">{getGPValues(w.turn_code)}</span>
                        </div>
                      </>
                    ) : (
                      <button
                        onClick={() => handleStartEdit(w)}
                        className="flex items-center gap-1.5 font-mono text-xs text-[#BF5AF2]/70 active:text-[#BF5AF2] transition-colors min-h-[44px]"
                      >
                        <Wand2 className="w-3.5 h-3.5 shrink-0" />
                        <span className="break-all text-left">{suggestTurnCode(w.word)}</span>
                      </button>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Desktop: table layout */}
      {filteredWords.length > 0 && (
        <Card padding="none" className="hidden md:block mb-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#D4D4D4] text-left text-xs text-[#888888] uppercase tracking-wider">
                  <th className="px-4 py-3">Word</th>
                  <th className="px-2 py-3 w-8"></th>
                  <th className="px-4 py-3">Turn Code</th>
                  <th className="px-4 py-3">Preview</th>
                  <th className="px-4 py-3">G-P Values</th>
                  <th className="px-2 py-3 w-8"></th>
                  <th className="px-4 py-3">Skill ID</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Teachable</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filteredWords.map(w => {
                  const isEditing = editingWordId === w.id;
                  const valid = isValidTurnCode(w.turn_code);
                  const suggestion = !w.turn_code ? suggestTurnCode(w.word) : null;

                  return (
                    <tr key={w.id} className="hover:bg-black/[0.03] transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm text-[#1A1A1A]">{w.word}</span>
                      </td>
                      <td className="px-2 py-3 text-center">
                        <button
                          onClick={() => handleToggleVerified(w)}
                          title={w.verified ? 'Verified — click to unverify' : 'Click to mark turn code as verified'}
                          className="w-3 h-3 rounded-full transition-all hover:scale-125 inline-block"
                          style={{ backgroundColor: w.verified ? '#30D158' : 'rgba(255,255,255,0.15)' }}
                        />
                      </td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <div className="space-y-1.5">
                            <input
                              type="text"
                              value={editTurnCode}
                              onChange={e => setEditTurnCode(e.target.value)}
                              placeholder="e.g. h, [e-green], l, p"
                              className="w-full px-2 py-1 bg-black/[0.03] border border-[#0A84FF] rounded text-[#1A1A1A] text-xs font-mono focus:outline-none"
                              autoFocus
                              onKeyDown={e => {
                                if (e.key === 'Enter') handleSave();
                                if (e.key === 'Escape') handleCancelEdit();
                              }}
                            />
                            {remainingPlaceholders > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {colorMappings.map(cm => (
                                  <button
                                    key={cm.color_name}
                                    onClick={() => handleColorChipClick(cm.color_name)}
                                    className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-black/[0.03] hover:bg-black/[0.06] transition-colors leading-tight"
                                    title={cm.color_name}
                                    style={{ color: cm.hex_code }}
                                  >
                                    {cm.color_name.slice(0, 2).toUpperCase()}
                                  </button>
                                ))}
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] text-[#888888]">Skill:</span>
                              {renderSkillSelect()}
                            </div>
                            {editTurnCode.trim() && (
                              <div className="flex items-center gap-2 text-xs text-[#888888]">
                                <span>Preview:</span> {renderTurnCodePreview(editTurnCode)}
                              </div>
                            )}
                          </div>
                        ) : w.turn_code ? (
                          <span className="font-mono text-xs text-[#3D3D3D]">{w.turn_code}</span>
                        ) : suggestion ? (
                          <button
                            onClick={() => handleStartEdit(w)}
                            className="group flex items-center gap-1.5 font-mono text-xs text-[#BF5AF2]/70 hover:text-[#BF5AF2] transition-colors"
                            title="Click to edit suggested turn code"
                          >
                            <Wand2 className="w-3 h-3 shrink-0" />
                            <span className="truncate max-w-[200px]">{suggestion}</span>
                          </button>
                        ) : (
                          <span className="font-mono text-xs text-[#D4D4D4]">&mdash;</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isEditing && editTurnCode.trim()
                          ? renderTurnCodePreview(editTurnCode)
                          : w.turn_code
                            ? renderTurnCodePreview(w.turn_code)
                            : suggestion
                              ? renderTurnCodePreview(suggestion)
                              : <span className="text-[#D4D4D4] text-xs">&mdash;</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs text-[#888888]">
                          {getGPValues(isEditing ? editTurnCode : w.turn_code)}
                        </span>
                      </td>
                      <td className="px-2 py-3 text-center">
                        <button
                          onClick={() => handleToggleSkillVerified(w)}
                          title={w.skill_verified ? 'Skill ID verified — click to unverify' : 'Click to mark skill ID as verified'}
                          className="w-3 h-3 rounded-full transition-all hover:scale-125 inline-block"
                          style={{ backgroundColor: w.skill_verified ? '#30D158' : 'rgba(255,255,255,0.15)' }}
                        />
                      </td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          renderSkillSelect()
                        ) : (
                          <span className="text-xs text-[#888888]">{w.skill_id || '\u2014'}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {valid ? (
                          <Badge variant="success">Valid</Badge>
                        ) : (
                          <Badge variant="error">Invalid</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleToggleTeachable(w)}
                          className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                            w.teachable === true
                              ? 'bg-[#FF9F0A]/20 text-[#FF9F0A] hover:bg-[#FF9F0A]/30'
                              : w.teachable === false
                              ? 'bg-[#111] text-white hover:bg-[#333]'
                              : 'bg-black/[0.04] text-[#AEAEB2] hover:bg-black/[0.08]'
                          }`}
                          title={w.teachable ? 'Mark as not teachable' : 'Mark as teachable'}
                        >
                          {w.teachable === true ? 'Yes' : w.teachable === false ? 'No' : '—'}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isEditing ? (
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={handleSave}
                              disabled={saving || !editTurnCode.trim()}
                              className="p-1.5 rounded hover:bg-[#30D158]/20 text-[#30D158] transition-colors disabled:opacity-30"
                              title="Save"
                            >
                              {saving ? <Spinner size="sm" /> : <Check className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="p-1.5 rounded hover:bg-[#FF453A]/20 text-[#FF453A] transition-colors"
                              title="Cancel"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleStartEdit(w)}
                            className="p-1.5 rounded hover:bg-black/[0.05] text-[#888888] hover:text-white transition-colors"
                            title="Edit turn code"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-sm text-[#888888]">
            Showing {currentPage * ITEMS_PER_PAGE + 1}&ndash;{Math.min((currentPage + 1) * ITEMS_PER_PAGE, totalCount)} of {totalCount.toLocaleString()}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 0}
              className="p-2 rounded-lg bg-black/[0.03] text-[#3D3D3D] hover:bg-black/[0.05] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-3 py-1.5 text-sm text-[#3D3D3D]">
              Page {currentPage + 1} of {totalPages}
            </span>
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
              className="p-2 rounded-lg bg-black/[0.03] text-[#3D3D3D] hover:bg-black/[0.05] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      </>}
    </div>
  );
};

export default LexiconPage;
